import * as R from 'Router/routes.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { fetchData } from 'utils';
import {
  PSD2Banks,
  PSD2ClientAccount,
  PSD2ClientConsentInformation,
  PSD2IdentificationPayment,
  PSD2ProcessResponse,
  PSD2SessionStatusResponse,
} from 'hooks/webapi/psd/usePsd2Offline';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';

export function usePSD2ProxyAccountsQuery(enabled = true) {
  const { showError } = useError();
  const { error } = useSmartlook();
  return useQuery<{
    bankAccounts: PSD2ClientAccount[];
  }>({
    queryKey: ['psd', 'proxy', 'accounts'],
    queryFn: async () => {
      try {
        const resp = await fetchData(`/proxy/psd/accounts`);

        return resp;
      } catch (e) {
        error('[usePSD2ProxyAccountsQuery][GET /proxy/psd/accounts]', e);
        showError();
        throw e;
      }
    },
    enabled,
  });
}

export function usePSD2ProxyBanksQuery(enabled = true) {
  const { showError } = useError();
  return useQuery<{
    banks: PSD2Banks[];
  }>({
    queryKey: ['psd2', 'proxy', 'banks'],
    queryFn: async () => {
      try {
        const resp = await fetchData('/proxy/psd/banks');

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
    enabled,
  });
}

export function usePSD2ProxyClientConsentsQuery(enabled = true) {
  const { showError } = useError();
  return useQuery<PSD2ClientConsentInformation>({
    queryKey: ['psd2', 'proxy', 'consents', 'search'],
    queryFn: async () => {
      try {
        const resp = await fetchData(`/proxy/psd/consents/search`);

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
    enabled,
  });
}

export function usePSD2ProxyInitPaymentMutation() {
  const { showError } = useError();
  return useMutation<
    PSD2IdentificationPayment,
    unknown,
    { clientIban: string }
  >({
    mutationFn: async ({ clientIban }) => {
      try {
        const resp = await fetchData(
          `/proxy/psd/payment?clientIban=${clientIban}`,
          { method: 'POST' },
        );

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
  });
}

export function usePSD2ProxySessionsDownloadMutation() {
  return useMutation<void, unknown>({
    mutationFn: async () => {
      await fetchData(`/proxy/psd/sessions/download`, {
        method: 'POST',
      });
    },
  });
}

export function usePSD2ProxySessionsInitMutation() {
  const { showError } = useError();
  return useMutation<
    void,
    unknown,
    {
      bankAccountChange: boolean;
      bankCode: string;
    }
  >({
    mutationFn: async (data) => {
      try {
        await fetchData('/proxy/psd/sessions/init', {
          method: 'POST',
          body: JSON.stringify({ ...data, psdSource: 'REGISTRATION' }),
        });
      } catch (e) {
        showError();
        throw e;
      }
    },
  });
}

export function usePSD2ProxyConfirmConsentsMutation() {
  const { showError } = useError();
  return useMutation<PSD2ProcessResponse, unknown, { consent: boolean }>({
    mutationFn: async ({ consent }) => {
      try {
        const resp = await fetchData(
          `/proxy/psd/sessions/process?consent=${consent}`,
          { method: 'POST' },
        );

        return resp;
      } catch (e) {
        showError();
        throw e;
      }
    },
  });
}

export function usePSD2ProxyStatusQuery(enabled = true) {
  const navigate = useNavigate();
  const { showError } = useError();
  const attempts = useRef<number>(1);
  return useQuery<PSD2SessionStatusResponse>({
    queryKey: ['psd2', 'proxy', 'status'],
    queryFn: async () => {
      try {
        const resp = await fetchData(`/proxy/psd/status`);
        attempts.current = 1; // Reset attempts value
        return resp;
      } catch (e) {
        attempts.current += 1;
        if (attempts.current > 15) navigate(R.OFFLINE_PSD2_ERROR); // MAX_ATTEMPTS = 15
        showError();
        throw e;
      }
    },
    enabled,
    refetchInterval: 5000,
    gcTime: 0,
  });
}
